// src/pages/TikpagExperience/TikpagExperienceParticipantes.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Award, Building2, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight,
  ChevronUp, CircleDollarSign, Crown, Filter, Medal, RefreshCcw, Search,
  Trophy, UserPlus, Users
} from "lucide-react";
import axios from "../../axiosConfig";
import { getPinImage } from "../../utils/pinAssets";
import "./TikpagExperienceParticipantes.css";

const MESES = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"
];
const hoje = new Date();
const dinheiro = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dataHora = (v) => {
  if (!v) return "-";
  const [d, h] = String(v).replace("T", " ").split(" ");
  const [a, m, dia] = String(d || "").slice(0, 10).split("-");
  return a && m && dia ? `${dia}/${m}/${a}${h ? ` ${h.slice(0,5)}` : ""}` : v;
};
const cnpj = (v) => {
  const n = String(v || "").replace(/\D/g, "");
  return n.length === 14 ? n.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : (v || "-");
};
const labelStatus = (v) => String(v || "").replaceAll("_"," ").toLowerCase().replace(/(^|\s)\S/g, l => l.toUpperCase());

export default function TikpagExperienceParticipantes() {
  const [aba, setAba] = useState("INSCRITOS");
  const [campanhas, setCampanhas] = useState([]);
  const [campanhaId, setCampanhaId] = useState("");
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [status, setStatus] = useState("");
  const [somentePremiados, setSomentePremiados] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [dadosInscritos, setDadosInscritos] = useState(null);
  const [dadosRanking, setDadosRanking] = useState(null);
  const [abertas, setAbertas] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCampanhas, setLoadingCampanhas] = useState(true);
  const [erro, setErro] = useState("");
  const limite = 25;

  const carregarCampanhas = useCallback(async () => {
    setLoadingCampanhas(true);
    setErro("");
    try {
      const { data } = await axios.get("/admin/experience/campanhas");
      const lista = Array.isArray(data?.campanhas) ? data.campanhas : [];
      setCampanhas(lista);
      setCampanhaId((atual) => {
        if (atual) return atual;
        const p = lista.find(i => i.status === "EM_ANDAMENTO")
          || lista.find(i => i.status === "INSCRICOES")
          || lista[0];
        return p ? String(p.id) : "";
      });
    } catch (err) {
      setErro(err.response?.data?.error || "Não foi possível carregar as campanhas.");
    } finally {
      setLoadingCampanhas(false);
    }
  }, []);

  useEffect(() => { carregarCampanhas(); }, [carregarCampanhas]);

  const carregarDados = useCallback(async () => {
    if (!campanhaId) return;
    setLoading(true);
    setErro("");
    try {
      if (aba === "INSCRITOS") {
        const { data } = await axios.get("/admin/experience/participantes", {
          params: {
            campanha_id: Number(campanhaId), pagina, limite,
            status: status || undefined, busca: buscaAplicada || undefined
          }
        });
        setDadosInscritos(data || null);
      } else {
        const { data } = await axios.get("/admin/experience/ranking", {
          params: {
            campanha_id: Number(campanhaId), ano: Number(ano), mes: Number(mes),
            pagina, limite, busca: buscaAplicada || undefined,
            somente_premiados: somentePremiados
          }
        });
        setDadosRanking(data || null);
      }
    } catch (err) {
      console.error("Erro ao carregar participantes Experience:", err);
      setErro(err.response?.data?.error || "Não foi possível carregar os dados da campanha.");
    } finally {
      setLoading(false);
    }
  }, [aba, campanhaId, pagina, status, buscaAplicada, ano, mes, somentePremiados]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  const dados = aba === "INSCRITOS" ? dadosInscritos : dadosRanking;
  const paginacao = dados?.paginacao || { pagina: 1, total_paginas: 1, total_registros: 0 };
  const participantes = Array.isArray(dadosInscritos?.participantes) ? dadosInscritos.participantes : [];
  const ranking = Array.isArray(dadosRanking?.ranking) ? dadosRanking.ranking : [];
  const rankingComMovimento = useMemo(
    () => ranking.some(i => Number(i.volume_apurado || 0) > 0),
    [ranking]
  );

  const cards = aba === "INSCRITOS"
    ? [
        ["Total de inscritos", dadosInscritos?.cards?.total_inscritos || 0, "Participantes cadastrados", Users],
        ["Inscritos ativos", dadosInscritos?.cards?.inscritos_ativos || 0, "Aptos a participar", CheckCircle2],
        ["Empresas", dadosInscritos?.cards?.empresas_participantes || 0, "Empresas com inscritos", Building2],
        ["Novos no mês", dadosInscritos?.cards?.novos_no_mes || 0, "Novas inscrições", UserPlus],
        ["Volume no mês", dinheiro(dadosInscritos?.cards?.volume_total_mes), "Operações consideradas", CircleDollarSign]
      ]
    : [
        ["Participantes", dadosRanking?.cards?.total_participantes || 0, "Na competência", Users],
        ["Premiados", dadosRanking?.cards?.total_premiados || 0, "Atingiram um PIN", Award],
        ["Volume total", dinheiro(dadosRanking?.cards?.volume_total), "Operações aprovadas", CircleDollarSign],
        ["Operações", dadosRanking?.cards?.total_operacoes || 0, "Quantidade considerada", Trophy],
        ["Taxa de premiação", `${Number(dadosRanking?.cards?.taxa_premiacao || 0).toLocaleString("pt-BR",{maximumFractionDigits:2})}%`, "Premiados sobre participantes", Medal]
      ];

  const aplicarBusca = () => { setPagina(1); setBuscaAplicada(busca.trim()); };
  const limparFiltros = () => {
    setBusca(""); setBuscaAplicada(""); setStatus(""); setSomentePremiados(false); setPagina(1);
  };
  const alternarLinha = (id) => setAbertas((atual) => ({ ...atual, [id]: !atual[id] }));

  return (
    <div className="experienceParticipantes-page">
      <header className="experienceParticipantes-header">
        <div>
          <span className="experienceParticipantes-kicker">Tikpag Experience</span>
          <h1>Participantes e ranking</h1>
          <p>Acompanhe inscrições, evolução, volume mensal e desempenho dos participantes.</p>
        </div>
        <button className="experienceParticipantes-refreshButton" onClick={carregarDados} disabled={loading || loadingCampanhas}>
          <RefreshCcw size={16} className={loading ? "experienceParticipantes-spin" : ""} />
          Atualizar
        </button>
      </header>

      {erro && <div className="experienceParticipantes-alert erro">{erro}</div>}

      <section className="experienceParticipantes-toolbar">
        <label className="experienceParticipantes-field">
          <span>Campanha</span>
          <select value={campanhaId} onChange={(e) => { setCampanhaId(e.target.value); setPagina(1); }} disabled={loadingCampanhas}>
            {!campanhas.length && <option value="">Nenhuma campanha</option>}
            {campanhas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </label>

        {aba === "RANKING" ? (
          <>
            <label className="experienceParticipantes-field compacto">
              <span>Ano</span>
              <input type="number" min="2020" max="2100" value={ano}
                onChange={(e) => { setAno(Number(e.target.value)); setPagina(1); }} />
            </label>
            <label className="experienceParticipantes-field">
              <span>Mês</span>
              <select value={mes} onChange={(e) => { setMes(Number(e.target.value)); setPagina(1); }}>
                {MESES.map((n, i) => <option key={n} value={i+1}>{n}</option>)}
              </select>
            </label>
          </>
        ) : (
          <label className="experienceParticipantes-field">
            <span>Status</span>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPagina(1); }}>
              <option value="">Todos</option>
              <option value="ATIVA">Ativa</option>
              <option value="CANCELADA">Cancelada</option>
            </select>
          </label>
        )}

        <div className="experienceParticipantes-search">
          <Search size={16} />
          <input value={busca} onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && aplicarBusca()}
            placeholder="Buscar cliente, ID, e-mail, CNPJ ou empresa" />
          <button type="button" onClick={aplicarBusca}>Buscar</button>
        </div>

        {aba === "RANKING" && (
          <label className="experienceParticipantes-checkFilter">
            <input type="checkbox" checked={somentePremiados}
              onChange={(e) => { setSomentePremiados(e.target.checked); setPagina(1); }} />
            <span>Somente premiados</span>
          </label>
        )}

        <button type="button" className="experienceParticipantes-clearButton" onClick={limparFiltros}>
          <Filter size={15} /> Limpar
        </button>
      </section>

      <div className="experienceParticipantes-tabs">
        <button className={aba === "INSCRITOS" ? "ativo" : ""} onClick={() => { setAba("INSCRITOS"); setPagina(1); }}>
          <Users size={16} /> Inscritos
        </button>
        <button className={aba === "RANKING" ? "ativo" : ""} onClick={() => { setAba("RANKING"); setPagina(1); }}>
          <Trophy size={16} /> Ranking
        </button>
      </div>

      <section className="experienceParticipantes-cards">
        {cards.map(([titulo, valor, legenda, Icone]) => (
          <article className="experienceParticipantes-card" key={titulo}>
            <div><Icone size={20} /></div>
            <span>{titulo}</span><strong>{valor}</strong><small>{legenda}</small>
          </article>
        ))}
      </section>

      {aba === "RANKING" && rankingComMovimento && (
        <section className="experienceParticipantes-rankingHighlights">
          <article className="experienceParticipantes-highlight lider">
            <div><Crown size={23} /></div><span>Líder atual</span>
            <h2>{dadosRanking?.lider?.usuario_nome}</h2>
            <strong>{dinheiro(dadosRanking?.lider?.volume_apurado)}</strong>
            <small>{dadosRanking?.lider?.pin_estimado?.nome || "Sem PIN"}</small>
          </article>

          <article className="experienceParticipantes-highlight proximo">
            <div><Award size={23} /></div><span>Mais próximo do Galaxy</span>
            <h2>{dadosRanking?.mais_proximo_galaxy?.usuario_nome || "Nenhum participante"}</h2>
            <strong>{dadosRanking?.mais_proximo_galaxy ? dinheiro(dadosRanking.mais_proximo_galaxy.falta_para_galaxy) : "-"}</strong>
            <small>Valor restante</small>
          </article>

          <article className="experienceParticipantes-distribution">
            <span>Distribuição por PIN</span>
            <div>
              {(dadosRanking?.distribuicao_pins || []).map(pin => {
                const img = getPinImage({ codigo: pin.pin_codigo });
                return (
                  <div key={pin.pin_id}>
                    {img ? <img src={img} alt={pin.pin_nome} /> : <Medal size={18} />}
                    <strong>{pin.total_participantes}</strong><small>{pin.pin_nome}</small>
                  </div>
                );
              })}
            </div>
          </article>
        </section>
      )}

      {aba === "RANKING" && !rankingComMovimento && !loading && (
        <section className="experienceParticipantes-noMovement">
          <Trophy size={28} />
          <div>
            <h2>Ranking ainda sem movimentação</h2>
            <p>Os destaques aparecerão quando houver operações aprovadas na competência.</p>
          </div>
        </section>
      )}

      <section className="experienceParticipantes-tablePanel">
        <div className="experienceParticipantes-tableHeader">
          <h2>{aba === "INSCRITOS" ? "Participantes inscritos" : "Ranking da competência"}</h2>
          <p>{Number(paginacao.total_registros || 0)} registros encontrados.</p>
        </div>

        <div className="experienceParticipantes-tableWrapper">
          {aba === "INSCRITOS" ? (
            <table className="experienceParticipantes-table">
              <thead>
                <tr><th/><th>Participante</th><th>Empresa</th><th>Inscrição</th><th>Operações</th><th>Volume</th><th>PIN estimado</th><th>Conquistas</th><th>Status</th></tr>
              </thead>
              <tbody>
                {participantes.map(p => {
                  const aberta = Boolean(abertas[p.usuario_id]);
                  const img = getPinImage(p.pin_estimado || p.proximo_pin);
                  return (
                    <React.Fragment key={p.usuario_id}>
                      <tr>
                        <td>
                          <button className="experienceParticipantes-expandButton" onClick={() => alternarLinha(p.usuario_id)}>
                            {aberta ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                          </button>
                        </td>
                        <td><strong>{p.usuario_nome}</strong><small>ID {p.usuario_id} • {p.usuario_email}</small></td>
                        <td><strong>{p.empresa?.nome || "Empresa não vinculada"}</strong><small>{cnpj(p.empresa?.cnpj)}</small></td>
                        <td>{dataHora(p.data_inscricao)}</td>
                        <td>{p.quantidade_operacoes_mes}</td>
                        <td><strong>{dinheiro(p.volume_mes)}</strong></td>
                        <td>
                          <div className="experienceParticipantes-pinCell">
                            {img && <img src={img} alt="" />}
                            <span>{p.pin_estimado?.nome || `Próximo: ${p.proximo_pin?.nome || "-"}`}</span>
                          </div>
                        </td>
                        <td>{p.quantidade_conquistas}</td>
                        <td><span className={`experienceParticipantes-status ${String(p.status_inscricao || "").toLowerCase()}`}>{labelStatus(p.status_inscricao)}</span></td>
                      </tr>
                      {aberta && (
                        <tr className="experienceParticipantes-expandedRow">
                          <td colSpan="9">
                            <div className="experienceParticipantes-expandedContent">
                              <div><span>Telefone</span><strong>{p.usuario_telefone || "-"}</strong></div>
                              <div><span>Localização</span><strong>{[p.usuario_cidade,p.usuario_estado].filter(Boolean).join(" / ") || "-"}</strong></div>
                              <div><span>Perfil</span><strong>{p.usuario_perfil || "-"}</strong></div>
                              <div><span>Próximo objetivo</span><strong>{p.proximo_pin?.nome || "Maior nível"}</strong></div>
                              <div><span>Falta para o próximo</span><strong>{dinheiro(p.falta_para_proximo)}</strong></div>
                              <div><span>Última conquista</span><strong>{dataHora(p.ultima_conquista_em)}</strong></div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="experienceParticipantes-table">
              <thead>
                <tr><th>Posição</th><th>Participante</th><th>Empresa</th><th>Operações</th><th>Volume</th><th>PIN</th><th>Próximo objetivo</th></tr>
              </thead>
              <tbody>
                {ranking.map(item => {
                  const img = getPinImage(item.pin_estimado || item.proximo_pin);
                  return (
                    <tr key={item.usuario_id}>
                      <td><span className={`experienceParticipantes-position position-${item.posicao}`}>{item.posicao <= 3 && <Medal size={17}/>} {item.posicao}º</span></td>
                      <td><strong>{item.usuario_nome}</strong><small>ID {item.usuario_id} • {item.usuario_email}</small></td>
                      <td><strong>{item.empresa?.nome || "Empresa não vinculada"}</strong><small>{cnpj(item.empresa?.cnpj)}</small></td>
                      <td>{item.quantidade_operacoes}</td>
                      <td><strong>{dinheiro(item.volume_apurado)}</strong></td>
                      <td><div className="experienceParticipantes-pinCell">{img && <img src={img} alt="" />}<span>{item.pin_estimado?.nome || "Sem PIN"}</span></div></td>
                      <td>{item.proximo_pin ? <><strong>{item.proximo_pin.nome}</strong><small>Faltam {dinheiro(item.falta_para_proximo)}</small></> : <span className="experienceParticipantes-maxLevel">Nível máximo</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {!loading && ((aba === "INSCRITOS" ? participantes.length : ranking.length) === 0) && (
            <div className="experienceParticipantes-empty">Nenhum registro encontrado.</div>
          )}
          {loading && (
            <div className="experienceParticipantes-loading">
              <RefreshCcw size={24} className="experienceParticipantes-spin" />
              <span>Carregando dados...</span>
            </div>
          )}
        </div>

        <footer className="experienceParticipantes-pagination">
          <span>Página {paginacao.pagina || 1} de {paginacao.total_paginas || 1}</span>
          <div>
            <button onClick={() => setPagina(a => Math.max(1, a - 1))} disabled={Number(paginacao.pagina || 1) <= 1 || loading}>
              <ChevronLeft size={16}/> Anterior
            </button>
            <button onClick={() => setPagina(a => Math.min(Number(paginacao.total_paginas || 1), a + 1))}
              disabled={Number(paginacao.pagina || 1) >= Number(paginacao.total_paginas || 1) || loading}>
              Próxima <ChevronRight size={16}/>
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}